import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, CircleIcon, MessageCircleIcon, MinusIcon, PlusIcon, ShoppingCartIcon, SmartphoneIcon, UtensilsCrossedIcon } from 'lucide-react';
import { RESTAURANT_FAQS, RESTAURANT_PROJECTS, SITE_CONFIG, WHATSAPP_MESSAGES } from '../config/siteConfig';
import { createWhatsAppUrl, trackEvent } from '../utils/analytics';
const NATUCAFE_PREVIEW = 'https://image.thum.io/get/width/700/crop/1200/noanimate/https://noizacr.com/r/natucafe-coffee-shop';
const benefits = ['Menú organizado por categorías', 'Carrito de compras', 'Pedido directo a WhatsApp', 'Lista en siete días', '₡120.000, pago único'];
const functions = [{
  title: 'Categorías claras',
  description: 'Tus clientes encuentran desayunos, bebidas, pizzas, combos u otras categorías sin buscar entre publicaciones.',
  icon: UtensilsCrossedIcon
}, {
  title: 'Carrito de compras',
  description: 'Cada persona puede armar su pedido completo antes de iniciar la conversación.',
  icon: ShoppingCartIcon
}, {
  title: 'Pedido listo para WhatsApp',
  description: 'El resumen del pedido llega directamente a tu WhatsApp para confirmar entrega o retiro.',
  icon: MessageCircleIcon
}];
const process = ['Nos enviás tu menú, precios, logo, fotos, horarios y WhatsApp.', 'Organizamos categorías y productos según tu operación.', 'Revisás la primera versión y realizamos los ajustes incluidos.', 'Publicamos tu menú para compartirlo en Instagram, Google y WhatsApp.'];
export function RestaurantsLanding() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  function restaurantClick(location: string) {
    trackEvent('whatsapp_restaurant_click', {
      cta_location: location,
      selected_product: 'Menú digital con carrito'
    });
  }
  return <main data-id="element-1295">
      <section className="relative overflow-hidden bg-[#050511] pb-16 pt-28 md:pb-20" data-id="element-1296">
        <div className="pointer-events-none absolute -right-32 top-20 h-80 w-80 rounded-full bg-[#a738f4]/10 blur-[100px]" data-id="element-1297" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.05fr_.95fr] lg:px-8" data-id="element-1298">
          <div className="min-w-0" data-id="element-1299">
            <p className="inline-flex rounded-full border border-[#20d6e8]/30 bg-[#20d6e8]/10 px-4 py-2 text-sm font-semibold text-[#b7f8ff]" data-id="element-1300">
              Para restaurantes, cafeterías y negocios gastronómicos
            </p>
            <h1 className="mt-6 max-w-3xl text-4xl font-bold leading-[1.12] text-white md:text-5xl lg:text-[3.45rem]" data-id="element-1301">
              Tu menú no debería vivir perdido entre{' '}
              <span className="text-gradient-cosmic" data-id="element-1302">
                publicaciones de Instagram.
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#a9a9ba]" data-id="element-1303">
              Organizamos tus productos en un menú digital con carrito para que
              tus clientes elijan y envíen el pedido completo directamente a tu
              WhatsApp.
            </p>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2" data-id="element-1304">
              {benefits.map(benefit => <li key={benefit} className="flex items-center gap-2.5 text-sm text-[#d8d8e4]" data-id="element-1305">
                  <CheckCircle2Icon className="h-5 w-5 flex-none text-[#20d6e8]" data-id="element-1306" />
                  {benefit}
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-1307">
              <a href={createWhatsAppUrl(WHATSAPP_MESSAGES.restaurant)} target="_blank" rel="noopener noreferrer" onClick={() => restaurantClick('restaurant_hero')} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#25d366] px-6 py-3 font-semibold text-[#06170c] transition-colors hover:bg-[#3ee47a] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1308">
                <MessageCircleIcon className="h-5 w-5" data-id="element-1309" />
                Quiero mi menú digital
              </a>
              <a href="#ejemplos" className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 px-6 py-3 font-semibold text-white transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20d6e8]" data-id="element-1310">
                Ver ejemplos de restaurantes
              </a>
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.55,
          delay: 0.1
        }} className="relative mx-auto w-full max-w-md" data-id="element-1311">
            <div className="mx-auto w-[70%] max-w-[290px] rounded-[2.25rem] border-[7px] border-[#27273b] bg-[#080812] p-2 shadow-2xl shadow-black/50" data-id="element-1312">
              <div className="mx-auto h-2 w-16 rounded-full bg-white/20" data-id="element-1313" />
              <div className="mt-3 aspect-[9/16] overflow-hidden rounded-[1.7rem]" data-id="element-1314">
                <img src={NATUCAFE_PREVIEW} alt="Vista móvil real del menú digital de NatuCafé" className="h-full w-full object-cover object-top" data-id="element-1315" />
              </div>
            </div>
            <div className="absolute -left-1 top-10 rounded-xl border border-[#a738f4]/30 bg-[#101022]/95 px-3 py-2.5 shadow-xl backdrop-blur sm:-left-8" data-id="element-1316">
              <p className="text-[10px] text-[#a9a9ba]" data-id="element-1317">Pedido completo</p>
              <p className="text-sm font-bold text-white" data-id="element-1318">directo a WhatsApp</p>
            </div>
            <div className="absolute -right-1 bottom-12 flex items-center gap-2 rounded-xl border border-[#20d6e8]/30 bg-[#101022]/95 px-3 py-2.5 text-xs font-semibold text-white shadow-xl backdrop-blur sm:-right-6" data-id="element-1319">
              <SmartphoneIcon className="h-4 w-4 text-[#20d6e8]" data-id="element-1320" />
              Listo para celular
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0c0c1c] py-6" data-id="element-1321">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-1322">
          <p className="text-sm font-medium text-[#d9d9e4]" data-id="element-1323">
            Negocios y proyectos que ya cuentan con soluciones digitales creadas
            por A-Z.
          </p>
        </div>
      </section>

      <section className="bg-[#0c0c1c] py-16 md:py-20" data-id="element-1324">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" data-id="element-1325">
          <div className="mx-auto max-w-3xl text-center" data-id="element-1326">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#20d6e8]" data-id="element-1327">
              Un pedido más claro desde el inicio
            </p>
            <h2 className="mt-3 text-3xl font-bold text-white md:text-5xl" data-id="element-1328">
              Que tu menú no dependa de una conversación interminable.
            </h2>
          </div>
          <div className="mt-9 grid gap-5 lg:grid-cols-2" data-id="element-1329">
            <article className="rounded-2xl border border-[#e92a88]/25 bg-[#101022] p-6" data-id="element-1330">
              <h3 className="text-xl font-bold text-white" data-id="element-1331">
                Entre publicaciones
              </h3>
              <ul className="mt-5 space-y-3 text-sm leading-relaxed text-[#c5c5d1]" data-id="element-1332">
                <li data-id="element-1333">
                  El cliente debe preguntar por precios, ingredientes y
                  disponibilidad.
                </li>
                <li data-id="element-1334">
                  Los productos se pierden entre historias, fotos y
                  publicaciones antiguas.
                </li>
                <li data-id="element-1335">
                  Recibís pedidos incompletos y tenés que pedir más detalles.
                </li>
              </ul>
            </article>
            <article className="rounded-2xl border border-[#20d6e8]/25 bg-[#101022] p-6" data-id="element-1336">
              <h3 className="text-xl font-bold text-white" data-id="element-1337">
                Con tu menú digital
              </h3>
              <ul className="mt-5 space-y-3 text-sm leading-relaxed text-[#d7d7e1]" data-id="element-1338">
                <li data-id="element-1339">
                  Productos, precios y categorías reunidos en un solo enlace.
                </li>
                <li data-id="element-1340">
                  El cliente selecciona lo que quiere antes de escribirte.
                </li>
                <li data-id="element-1341">
                  El pedido llega a WhatsApp con la información organizada.
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section id="incluye" className="bg-[#050511] py-16 md:py-20" data-id="element-1342">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-1343">
          <div className="max-w-3xl" data-id="element-1344">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#20d6e8]" data-id="element-1345">
              Funciones pensadas para gastronomía
            </p>
            <h2 className="mt-3 text-3xl font-bold text-white md:text-5xl" data-id="element-1346">
              Tu menú ordenado para elegir y enviar.
            </h2>
          </div>
          <div className="mt-9 grid gap-5 md:grid-cols-3" data-id="element-1347">
            {functions.map(item => {
            const Icon = item.icon;
            return <article key={item.title} className="rounded-2xl border border-white/10 bg-[#101022] p-6" data-id="element-1348">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#a738f4]/15" data-id="element-1349">
                    <Icon className="h-5 w-5 text-[#d58aff]" data-id="element-1350" />
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-white" data-id="element-1351">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#a9a9ba]" data-id="element-1352">
                    {item.description}
                  </p>
                </article>;
          })}
          </div>
        </div>
      </section>

      <section id="ejemplos" className="bg-[#0c0c1c] py-16 md:py-20" data-id="element-1353">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-1354">
          <div className="max-w-3xl" data-id="element-1355">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#20d6e8]" data-id="element-1356">
              Ejemplos gastronómicos
            </p>
            <h2 className="mt-3 text-3xl font-bold text-white md:text-5xl" data-id="element-1357">
              Restaurantes que ya muestran sus productos con claridad.
            </h2>
          </div>
          <div className="mt-9 grid gap-6 md:grid-cols-2" data-id="element-1358">
            {RESTAURANT_PROJECTS.map((project, index) => <motion.article key={project.name} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="overflow-hidden rounded-2xl border border-white/10 bg-[#101022]" data-id="element-1359">
                <div className="relative aspect-[16/9] overflow-hidden" data-id="element-1360">
                  <img src={project.previewUrl} alt={`Vista previa del menú digital de ${project.name}`} loading="lazy" className="h-full w-full object-cover object-top" data-id="element-1361" />
                  <div className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-[#050511]/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white backdrop-blur" data-id="element-1362">
                    <CircleIcon className="h-2 w-2 fill-[#25d366] text-[#25d366]" data-id="element-1363" />
                    Sitio activo
                  </div>
                  {project.name === 'NatuCafé' && <p className="absolute bottom-4 left-4 rounded-full border border-[#67d19b]/50 bg-[#07150f]/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#a7f0c7] backdrop-blur" data-id="element-1364">
                      Ejemplo recomendado para restaurantes
                    </p>}
                </div>
                <div className="p-6" data-id="element-1365">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{
                color: project.accent
              }} data-id="element-1366">
                    {project.category}
                  </p>
                  <h3 className="mt-2 text-2xl font-bold text-white" data-id="element-1367">
                    {project.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#a9a9ba]" data-id="element-1368">
                    {project.description}
                  </p>
                  <a href={project.url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('portfolio_project_click', {
                cta_location: 'restaurant_portfolio',
                project_name: project.name
              })} className="mt-5 inline-flex min-h-11 items-center text-sm font-semibold text-white hover:text-[#20d6e8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20d6e8]" data-id="element-1369">
                    Ver proyecto
                  </a>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section id="proceso" className="bg-[#050511] py-16 md:py-20" data-id="element-1370">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8" data-id="element-1371">
          <div className="text-center" data-id="element-1372">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#20d6e8]" data-id="element-1373">
              Un proceso simple
            </p>
            <h2 className="mt-3 text-3xl font-bold text-white md:text-5xl" data-id="element-1374">
              De tu menú actual a un enlace listo para compartir.
            </h2>
          </div>
          <ol className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1375">
            {process.map((step, index) => <li key={step} className="rounded-xl border border-white/10 bg-[#101022] p-5" data-id="element-1376">
                <span className="text-sm font-bold text-[#20d6e8]" data-id="element-1377">
                  Paso {index + 1}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-[#c9c9d6]" data-id="element-1378">
                  {step}
                </p>
              </li>)}
          </ol>
        </div>
      </section>

      <section id="precio" className="bg-[#0c0c1c] py-16 md:py-20" data-id="element-1379">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-1380">
          <article className="rounded-2xl border border-[#a738f4]/35 bg-[#101022] p-6 md:p-8" data-id="element-1381">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#d58aff]" data-id="element-1382">
              Precio claro
            </p>
            <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl" data-id="element-1383">
              Menú digital funcional para tu negocio gastronómico
            </h2>
            <div className="mt-5 flex flex-wrap items-end gap-x-4 gap-y-1" data-id="element-1384">
              <p className="font-orbitron text-5xl font-bold text-white" data-id="element-1385">
                {SITE_CONFIG.product.price}
              </p>
              <p className="pb-1 text-sm font-medium text-[#c9c9d6]" data-id="element-1386">
                Pago único
              </p>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#d4d4df]" data-id="element-1387">
              {SITE_CONFIG.product.paymentPolicy}
            </p>
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2" data-id="element-1388">
              {['Categorías y productos iniciales', 'Carrito hacia WhatsApp', 'Ubicación, horarios y redes', 'Dominio y hosting por un año', 'Diseño adaptable a celular', 'Dos rondas de revisión'].map(item => <li key={item} className="flex gap-2 text-sm text-[#d4d4df]" data-id="element-1389">
                  <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-[#20d6e8]" data-id="element-1390" />
                  {item}
                </li>)}
            </ul>
            <a href={createWhatsAppUrl(WHATSAPP_MESSAGES.restaurant)} target="_blank" rel="noopener noreferrer" onClick={() => restaurantClick('restaurant_price')} className="mt-7 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#25d366] px-6 py-4 font-semibold text-[#06170c] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1391">
              <MessageCircleIcon className="h-5 w-5" data-id="element-1392" />
              Quiero mi menú digital
            </a>
            <p className="mt-4 rounded-lg border-l-2 border-[#e92a88] bg-[#e92a88]/5 p-4 text-sm text-[#d6c6d0]" data-id="element-1393">
              Funcionalidades especiales se cotizan por separado.
            </p>
          </article>
        </div>
      </section>

      <section id="preguntas" className="bg-[#050511] py-16 md:py-20" data-id="element-1394">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8" data-id="element-1395">
          <div className="text-center" data-id="element-1396">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#20d6e8]" data-id="element-1397">
              Información clara
            </p>
            <h2 className="mt-3 text-3xl font-bold text-white md:text-5xl" data-id="element-1398">
              Preguntas frecuentes
            </h2>
          </div>
          <div className="mt-8 space-y-3" data-id="element-1399">
            {RESTAURANT_FAQS.map((faq, index) => {
            const isOpen = index === openFaq;
            return <article key={faq.question} className="overflow-hidden rounded-xl border border-white/10 bg-[#101022]" data-id="element-1400">
                  <button type="button" aria-expanded={isOpen} onClick={() => {
                setOpenFaq(isOpen ? null : index);
                if (!isOpen) {
                  trackEvent('faq_open', {
                    cta_location: 'restaurant_faq'
                  });
                }
              }} className="flex min-h-14 w-full items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#20d6e8]" data-id="element-1401">
                    <span data-id="element-1402">{faq.question}</span>
                    {isOpen ? <MinusIcon className="h-5 w-5 flex-none text-[#20d6e8]" data-id="element-1403" /> : <PlusIcon className="h-5 w-5 flex-none text-[#a9a9ba]" data-id="element-1404" />}
                  </button>
                  {isOpen && <p className="px-5 pb-5 leading-relaxed text-[#a9a9ba]" data-id="element-1405">
                      {faq.answer}
                    </p>}
                </article>;
          })}
          </div>
        </div>
      </section>

      <section className="bg-[#0c0c1c] py-16 md:py-20" data-id="element-1406">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8" data-id="element-1407">
          <div className="rounded-2xl border border-[#25d366]/30 bg-[#101022] px-6 py-10 md:px-10" data-id="element-1408">
            <h2 className="text-3xl font-bold text-white md:text-4xl" data-id="element-1409">
              Tu menú puede estar listo para recibir pedidos.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-[#a9a9ba]" data-id="element-1410">
              Con categorías, carrito y un pedido claro directo a tu WhatsApp.
            </p>
            <a href={createWhatsAppUrl(WHATSAPP_MESSAGES.restaurant)} target="_blank" rel="noopener noreferrer" onClick={() => restaurantClick('restaurant_final')} className="mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#25d366] px-6 py-3 font-semibold text-[#06170c] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1411">
              <MessageCircleIcon className="h-5 w-5" data-id="element-1412" />
              Quiero mi menú digital
            </a>
          </div>
        </div>
      </section>
    </main>;
}